import React, { useState, useEffect, useRef } from 'react';
import { Storage } from 'aws-amplify';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import { Mention, MentionsInput } from 'react-mentions';
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import usePromise from 'hooks/usePromise';
import useSnack from 'hooks/useSnack';
import uuidv4 from 'uuidv4';
import FilePreview from './FilePreview';

const FILE_TYPES =
  '.jpg,.png,.gif,.jpeg,.pdf,.mp4,.mpeg,.webm,.ogg,.wav,.vcf,.doc,.docx,.xls,.xlsx';

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow:
      '0px -5px 47px rgba(44, 9, 12, 0.05)',
    padding: 8,
    zIndex: 1,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  input: {
    'paddingLeft': 8,
    // 'paddingTop': 10,
    'paddingBottom': 10,
    '& textarea': {
      border: 'none',
      outline: 'none',
    },
  },
  actions: {
    display: 'flex',
  },
  upload: {
    '& input': {
      display: 'none',
    },
  },
}));

const mentionStyles = {
  'control': {
    backgroundColor: '#fff',
    color: '#626C82',
    fontSize: 16,
    fontWeight: 'normal',
  },
  '&multiLine': {
    highlighter: {
      padding: 0,
    },
  },
  'suggestions': {
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 14,
    },
    item: {
      'padding': '5px 15px',
      'borderBottom': '1px solid rgba(0,0,0,0.15)',
      '&focused': {
        backgroundColor: '#cee4e5',
      },
    },
  },
};

const Composer = ({ users, onSend, placeholder, tab = 'default' }) => {
  const classes = useStyles();
  const snack = useSnack();
  const [text, setText] = useState('');
  const [file, setFile] = useState();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileKey, setFileKey] = useState();
  const fileRef = useRef();

  useEffect(() => {
    setProgress(0);
  }, [file]);

  const [handleSubmit, loading] = usePromise(() =>
    onSend(text, fileKey).then(
      () => {
        setText('');
        setFile();
        setProgress(0);
        setFileKey();
      },
      (e) => snack.error(e.message),
    ),
  );

  const onFileSelect = (e) => {
    const { files } = e.target;
    const f = files[0];
    setFile(f);
    fileRef.current.value = '';

    const key = uuidv4() + '_' + f.name;
    setUploading(true);
    const contentType = f.type
    Storage.put(key, f, {
      contentType,
      progressCallback: (progress) => {
        const pc = (progress.loaded / progress.total) * 100;
        setProgress(`Uploading... ${Math.round(pc)}%`);
      },
    })
      .then((result) => {
        setFileKey(result.key);
        setUploading(false);
      })
      .catch((err) => {
        setUploading(false);
        snack.error(err.message);
      });
  };

  const onDeleteFile = () => {
    setFile();
    if (fileKey) {
      Storage.remove(fileKey).catch(e => console.log(e));
    }
  };

  const onPasteCapture = (e) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    let blob = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") === 0) {
        blob = items[i].getAsFile();
      }
    }
    if (blob !== null) {
      onFileSelect({
        target: {
          files: [blob]
        }
      });
    }
  }

  return (
    <div className={classes.root}>
      {file && (
        <FilePreview
          loading={uploading}
          progress={progress}
          file={file}
          onDelete={onDeleteFile}
        />
      )}
      <div className={classes.inputContainer}>
        <Box flex={1} style={{ maxHeight: 300, overflowY: 'auto' }}>
          <MentionsInput
            allowSuggestionsAboveCursor
            className={classes.input}
            placeholder={placeholder}
            value={text}
            onChange={(_, value) => setText(value)}
            style={mentionStyles}
            onPaste={onPasteCapture}
            suggestionsPortalHost={document.getElementById('chat-room-root')}
          >
            <Mention
              trigger="@"
              displayTransform={(id, display) => `@${display}`}
              data={users}
              style={{
                fontSize: 16,
              }}
            />
          </MentionsInput>
        </Box>
        <div className={classes.actions}>
          <div className={classes.upload}>
            <IconButton component="label" htmlFor={tab}>
              <AttachFileIcon />
            </IconButton>
            <input
              accept={FILE_TYPES}
              ref={fileRef}
              id={tab}
              type="file"
              onChange={onFileSelect}
            />
          </div>
          <IconButton
            color="primary"
            disabled={loading || uploading || !(text || fileKey)}
            onClick={handleSubmit}
          >
            <SendIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default Composer;
